import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  addDoc,
  Timestamp,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { BadgePercent } from "lucide-react";
import LogoLoader from "../components/LogoLoader";
import SimpleMarkdown from "../components/SimpleMarkdown";
import { useAuthContext } from "../hooks/useAuthContext";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { DEALERSHIP_OPTIONS, getDealershipByEmail } from "../data/dealerships";
import { Button } from "../components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const DynamicPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [pageNotFound, setPageNotFound] = useState(false);
  const [interestSource, setInterestSource] = useState("");
  const navigate = useNavigate();

  // Всички логнати потребители = администратори
  const { user, authIsReady } = useAuthContext();

  const contactFields = useMemo(
    () => pageData?.contactFields || [],
    [pageData?.contactFields],
  );
  const formSchema = useMemo(() => {
    const requiredString = (message) => z.string().trim().min(1, message);
    const emailField = requiredString("Моля, въведете имейл-адрес").regex(
      /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
      "Невалиден имейл-адрес",
    );
    const phoneField = requiredString("Моля, въведете телефонен номер").regex(
      /^[+]?[()]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      "Невалиден телефонен номер",
    );

    return z.object({
      name: contactFields.includes("имена")
        ? requiredString("Моля, въведете Вашето име")
        : z.string().optional().default(""),
      email: contactFields.includes("имейл")
        ? emailField
        : z.string().optional().default(""),
      phone: contactFields.includes("телефон")
        ? phoneField
        : z.string().optional().default(""),
      message: contactFields.includes("съобщение")
        ? requiredString("Моля, въведете Вашето съобщение")
        : z.string().optional().default(""),
      dealership: contactFields.includes("предпочитано представителство")
        ? requiredString("Моля, изберете представителство")
        : z.string().optional().default(""),
    });
  }, [contactFields]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      dealership: "",
    },
  });

  useEffect(() => {
    reset({
      name: "",
      email: "",
      phone: "",
      message: "",
      dealership: "",
    });
    setInterestSource("");
  }, [slug, reset]);

  const marketingCampaign = async (data, slug) => {
    try {
      const selectedDealership = getDealershipByEmail(data.dealership);

      await addDoc(collection(db, slug), {
        name: data.name ?? "", // Ако липсва, записва празен низ
        email: data.email ?? "",
        phone: data.phone ?? "",
        dealership: data.dealership ?? "",
        message: data.message ?? "",
        interestSource,
        date: Timestamp.fromDate(new Date()),
        title: pageData.title ?? "",
      });

      await submitLeadEmail({
        type: LEAD_TYPES.CAMPAIGN_TESTDRIVE,
        customer: {
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
        },
        dealership: {
          email: data.dealership ?? "",
          label: selectedDealership?.label ?? "",
        },
        vehicle: {
          model: pageData.title ?? "",
        },
        campaign: {
          slug,
        },
        source: {
          pageUrl: window.location.href,
          interestSource,
        },
        message: data.message ?? "",
        title: pageData.formTitle || pageData.title || "Запитване",
      });

      if (pageData.formGtagSendTo && window.gtag) {
        window.gtag("event", "conversion", {
          send_to: pageData.formGtagSendTo,
        });
      }

      if (pageData.formMetaEventName && window.fbq) {
        window.fbq(
          pageData.formMetaEventType || "track",
          pageData.formMetaEventName,
          {
            page_slug: slug,
            page_title: pageData.title ?? "",
          },
        );
      }

      navigate("/thank-you-testdrive");
    } catch (error) {
      console.error("Грешка при запис в Firebase:", error);
    }
  };

  // Извличане на данни за текущата страница (slug)
  useEffect(() => {
    const fetchData = async () => {
      setIsPageLoading(true);
      setPageNotFound(false);

      const q = query(
        collection(db, "LandingPages"),
        where("slug", "==", slug),
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setPageData(data);
        setPageNotFound(false);
      } else {
        console.log("Страницата не беше намерена");
        setPageData(null);
        setPageNotFound(true);
      }

      setIsPageLoading(false);
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    if (!pageData || typeof window === "undefined") {
      return;
    }

    if (window.location.hash !== "#templateCampaignForm") {
      return;
    }

    let timeoutIds = [];

    const scrollToForm = () => {
      const formElement = document.getElementById("templateCampaignForm");
      if (!formElement) {
        return;
      }

      const top = formElement.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: "smooth" });
    };

    const scheduleScrollAttempts = () => {
      scrollToForm();

      [250, 600].forEach((delay) => {
        const timeoutId = window.setTimeout(() => {
          scrollToForm();
        }, delay);

        timeoutIds.push(timeoutId);
      });
    };

    if (document.readyState === "complete") {
      scheduleScrollAttempts();
    } else {
      window.addEventListener("load", scheduleScrollAttempts);
    }

    return () => {
      window.removeEventListener("load", scheduleScrollAttempts);
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [pageData]);

  if (isPageLoading) {
    return <LogoLoader />;
  }

  if (pageNotFound) {
    return (
      <p className="my-12 text-lg text-center text-red-600">
        Страницата не беше намерена
      </p>
    );
  }

  document.title = "FORD - " + pageData.title;

  const scrollToContactForm = () => {
    if (typeof window === "undefined") {
      return;
    }

    const formElement = document.getElementById("templateCampaignForm");
    if (!formElement) {
      return;
    }

    const top = formElement.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Рендиране на блоковете
  const renderBlocks = () => {
    return (pageData.blocks || []).map((block, index) => (
      <section
        key={index}
        className={`flex flex-col-reverse ${
          index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
        } w-full mb-12 lg:mb-20 first:mt-12 lg:first:mt-20`}
      >
        <div className="p-8 lg:w-1/2">
          <h3 className="text-xl font-bold tracking-wide uppercase text-fordBlue">
            {block.title}
          </h3>
          {block.promoText ? (
            <div className="inline-flex flex-col items-start gap-3 px-5 py-4 mt-3 text-lg font-normal leading-snug rounded-[8px] bg-[rgb(241,241,241)] text-fordBlue">
              <BadgePercent
                size={26}
                strokeWidth={1.8}
                className="text-[rgb(23,0,244)]"
              />
              <SimpleMarkdown
                content={block.promoText}
                className="text-fordBlue"
                paragraphClassName="m-0"
                headingClassName="m-0 text-left text-lg font-semibold tracking-wide text-fordBlue"
                listClassName={(ordered) =>
                  `m-0 pl-5 text-left ${ordered ? "list-decimal" : "list-disc"}`
                }
                listItemClassName="mt-1 first:mt-0"
                linkClassName="font-semibold underline underline-offset-4 text-fordBlue"
                strongClassName="font-semibold text-fordBlue"
                emClassName="italic text-fordBlue"
              />
            </div>
          ) : null}
          <p className="mt-4 text-fordBlue">{block.text}</p>
          {/* Първи бутон */}
          {block.button1Text && block.button1Url && (
            <a
              href={block.button1Url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 mt-4 text-white rounded-full bg-fordBlue hover:bg-fordLight"
            >
              {block.button1Text}
            </a>
          )}
          {/* Втори бутон */}
          {block.button2Text && block.button2Url && (
            <a
              href={block.button2Url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 mt-4 ml-2 border rounded-full hover:border-fordLight text-fordBlue border-fordBlue hover:text-white hover:bg-fordLight"
            >
              {block.button2Text}
            </a>
          )}
          {/* Бутон за скрол до формата */}
          {block.buttonScrollTitle && (
            <button
              type="button"
              onClick={() => {
                setInterestSource(block.interestSource || block.title || "");
                scrollToContactForm();
              }}
              className="inline-block px-4 py-2 mt-4 ml-2 border rounded-full hover:border-fordLight text-fordBlue border-fordBlue hover:text-white hover:bg-fordLight"
            >
              {block.buttonScrollTitle}
            </button>
          )}
        </div>
        <img
          className="object-cover w-full rounded-2xl lg:w-1/2"
          src={block.imageUrl}
          alt={block.title}
        />
      </section>
    ));
  };

  // Рендиране на контактната форма
  const renderContactForm = () => {
    return (
      <form
        onSubmit={handleSubmit((data) => marketingCampaign(data, slug))}
        id="templateCampaignForm"
        className="mx-auto w-full max-w-3xl px-6 py-12 sm:px-8"
        noValidate
      >
        <h2 className="text-xl tracking-wider text-center uppercase text-fordBlue">
          {pageData.formTitle}
        </h2>

        <FieldGroup className="mt-8 gap-5">
          {contactFields.includes("имена") ? (
            <Field data-invalid={!!errors.name}>
              <FieldLabel htmlFor="name">Име</FieldLabel>
              <Input
                id="name"
                aria-invalid={!!errors.name}
                className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                {...register("name")}
              />
              <FieldError>{errors.name?.message}</FieldError>
            </Field>
          ) : null}

          {contactFields.includes("имейл") ? (
            <Field data-invalid={!!errors.email}>
              <FieldLabel htmlFor="email">Имейл</FieldLabel>
              <Input
                id="email"
                type="email"
                aria-invalid={!!errors.email}
                className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                {...register("email")}
              />
              <FieldError>{errors.email?.message}</FieldError>
            </Field>
          ) : null}

          {contactFields.includes("телефон") ? (
            <Field data-invalid={!!errors.phone}>
              <FieldLabel htmlFor="phone">Телефон</FieldLabel>
              <Input
                id="phone"
                type="text"
                aria-invalid={!!errors.phone}
                className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                {...register("phone")}
              />
              <FieldError>{errors.phone?.message}</FieldError>
            </Field>
          ) : null}

          {contactFields.includes("съобщение") ? (
            <Field data-invalid={!!errors.message}>
              <FieldLabel htmlFor="message">Съобщение</FieldLabel>
              <Textarea
                id="message"
                rows={6}
                aria-invalid={!!errors.message}
                className="min-h-[140px] rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 py-3 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                {...register("message")}
              />
              <FieldError>{errors.message?.message}</FieldError>
            </Field>
          ) : null}

          {contactFields.includes("предпочитано представителство") ? (
            <Field data-invalid={!!errors.dealership}>
              <FieldLabel>Предпочитано представителство</FieldLabel>
              <Controller
                name="dealership"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                      aria-invalid={!!errors.dealership}
                    >
                      <SelectValue placeholder="Моля, изберете" />
                    </SelectTrigger>
                    <SelectContent className="bg-white font-fordAntennaRegular">
                      {DEALERSHIP_OPTIONS.map((dealership) => (
                        <SelectItem
                          key={dealership.email}
                          value={dealership.email}
                        >
                          {dealership.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldError>{errors.dealership?.message}</FieldError>
            </Field>
          ) : null}
        </FieldGroup>

        <div className="flex justify-center pt-8">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="min-h-11 rounded-full bg-fordBlue px-10 text-white hover:bg-fordLight"
          >
            {isSubmitting ? "Изпращане..." : "Изпрати"}
          </Button>
        </div>
      </form>
    );
  };

  // Ако не е готов auth, показваме loader
  if (!authIsReady) {
    return <LogoLoader />;
  }

  // Ако страницата не е публикувана и няма logнат user => не се вижда
  if (!pageData.isPublished && !user) {
    return (
      <p className="my-12 text-lg text-center text-red-600">
        Тази страница не е публична
      </p>
    );
  }

  const heroButtons = [
    {
      label: pageData.primaryButtonLabel,
      action: pageData.primaryButtonAction || "url",
      url: pageData.primaryButtonUrl,
      target: pageData.primaryButtonTarget || "_self",
      style: pageData.primaryButtonStyle || "solid",
    },
    {
      label: pageData.secondaryButtonLabel,
      action: pageData.secondaryButtonAction || "url",
      url: pageData.secondaryButtonUrl,
      target: pageData.secondaryButtonTarget || "_self",
      style: pageData.secondaryButtonStyle || "outlined",
    },
  ].filter(
    (button) =>
      button.label &&
      (button.action === "scroll-to-form" || Boolean(button.url))
  );

  const buttonsToRender =
    heroButtons.length > 0
      ? heroButtons
      : pageData.buttonUrl
        ? [
            {
              label: "Виж наличните автомобили",
              action: "url",
              url: pageData.buttonUrl,
              target: "_self",
              style: "solid",
            },
          ]
        : [];

  return (
    <div className="font-fordAntennaRegular">
      {pageData.stickyContactButtonLabel ? (
        <a
          href="#templateCampaignForm"
          className="fixed right-0 top-1/2 z-50 hidden -translate-y-1/2 rounded-l-2xl bg-fordBlue px-4 py-5 text-center text-sm tracking-[0.18em] text-white shadow-xl transition hover:bg-fordLight lg:block [writing-mode:vertical-rl]"
        >
          {pageData.stickyContactButtonLabel}
        </a>
      ) : null}

      {/* <img src={pageData.desktopImage} alt={slug} /> */}
      <picture>
        {/* За мобилни устройства */}
        <source
          srcSet={pageData.mobileImage || pageData.desktopImage}
          media="(max-width: 768px)"
        />
        {/* За десктоп устройства */}
        <img src={pageData.desktopImage} alt={slug} className="w-full" />
      </picture>

      <section className="text-5xl font-bold tracking-wide text-center text-white uppercase">
        <h1 className="px-12 pt-10 text-xl md:text-3xl bg-fordBlue">
          {pageData.title}
        </h1>
      </section>
      <section className="py-8 text-center text-white bg-fordBlue">
        <p className="max-w-4xl mx-auto text-lg">{pageData.description}</p>
      </section>

      {buttonsToRender.length > 0 ? (
        <section className="px-6 py-10 bg-white">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {buttonsToRender.map((button, index) => (
              <a
                key={`${button.label}-${index}`}
                className={`px-8 py-4 tracking-wider rounded-full transition ${
                  button.style === "outlined"
                    ? "border border-fordBlue text-fordBlue hover:bg-fordBlue hover:text-white"
                    : "bg-fordBlue text-white hover:bg-fordLight"
                }`}
                href={
                  button.action === "scroll-to-form"
                    ? "#templateCampaignForm"
                    : button.url
                }
                target={button.action === "url" ? button.target : undefined}
                rel={
                  button.action === "url" && button.target === "_blank"
                    ? "noreferrer"
                    : undefined
                }
              >
                {button.label}
              </a>
            ))}
          </div>
        </section>
      ) : null}

      {pageData.description2 ? (
        <section className="px-6 py-8 bg-white">
          <SimpleMarkdown
            content={pageData.description2}
            className="max-w-4xl mx-auto text-lg leading-8 text-fordBlue"
          />
        </section>
      ) : null}

      {(pageData.blocksSectionTitle || (pageData.blocks || []).length > 0) && (
        <section className="w-full mx-auto">
          {pageData.blocksSectionTitle ? (
            <h2 className="px-6 py-8 text-3xl md:text-4xl font-semibold tracking-wide text-center uppercase text-fordBlue">
              {pageData.blocksSectionTitle}
            </h2>
          ) : null}
          {renderBlocks()}
        </section>
      )}

      <section className="w-full xl:w-[1300px] mx-auto py-8">
        {renderContactForm()}
      </section>

      {pageData.disclaimer && (
        <p className="ml-12 text-sm text-gray-500">{pageData.disclaimer}</p>
      )}
    </div>
  );
};

export default DynamicPage;
